package redmcrosco.alex.farmatch.ui

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import kotlinx.coroutines.launch
import redmcrosco.alex.farmatch.api.FarmatchApi
import redmcrosco.alex.farmatch.models.*
import redmcrosco.alex.farmatch.utils.*

import redmcrosco.alex.farmatch.services.LocationManager
import kotlinx.datetime.*
import kotlinx.coroutines.flow.collectLatest

class AuthViewModel : ViewModel() {
    private val api = FarmatchApi()

    // --- LOGICA GEOGRAFICA ---
    var isLocationDataLoaded by mutableStateOf(false)
    var listaRegioni by mutableStateOf<List<String>>(emptyList())
    var listaProvince by mutableStateOf<List<String>>(emptyList())
    var listaComuni by mutableStateOf<List<String>>(emptyList())
    var listaCap by mutableStateOf<List<String>>(emptyList())

    // Mapping per cleanup selezioni
    var mappingProvinciaRegione = mutableMapOf<String, String>()
    var mappingComuneProvincia = mutableMapOf<String, String>()

    init {
        loadRegioni()
    }

    fun loadRegioni() {
        viewModelScope.launch {
            println("DEBUG: Tentativo caricamento regioni...")
            try {
                val regioni = api.getRegioni()
                if (regioni.isNotEmpty()) {
                    listaRegioni = regioni
                    isLocationDataLoaded = true
                    println("DEBUG: Caricate ${regioni.size} regioni")
                } else {
                    println("DEBUG: Lista regioni ricevuta vuota dal server")
                }
            } catch (e: Exception) {
                println("DEBUG: Error loading regions: ${e.message}")
            }
        }
    }

    fun updateProvincePerRegione(regione: String) {
        viewModelScope.launch {
            listaProvince = api.getProvince(regione)
            // Selezionando una nuova regione singola, resettiamo comuni e cap caricati
            listaComuni = emptyList()
            listaCap = emptyList()
        }
    }

    fun updateProvincePerRegioni(regioni: List<String>) {
        listaProvince = emptyList()
        viewModelScope.launch {
            if (regioni.isEmpty()) {
                // Già svuotata sopra
            } else {
                val allProvince = mutableListOf<String>()
                regioni.forEach { r ->
                    val provs = api.getProvince(r)
                    provs.forEach { p -> mappingProvinciaRegione[p] = r }
                    allProvince.addAll(provs)
                }
                listaProvince = allProvince.distinct().sorted()
            }
            listaComuni = emptyList()
            listaCap = emptyList()
        }
    }

    fun updateComuniPerProvince(province: List<String>) {
        listaComuni = emptyList()
        viewModelScope.launch {
            if (province.isEmpty()) {
                // Già svuotata sopra
            } else {
                val allComuni = mutableListOf<String>()
                province.forEach { p ->
                    val coms = api.getComuni(p)
                    coms.forEach { c -> mappingComuneProvincia[c] = p }
                    allComuni.addAll(coms)
                }
                listaComuni = allComuni.distinct().sorted()
            }
            listaCap = emptyList()
        }
    }

    fun updateComuniPerProvincia(provincia: String) {
        viewModelScope.launch {
            listaComuni = api.getComuni(provincia)
            listaCap = emptyList()
        }
    }

    fun updateCapPerComune(comune: String) {
        viewModelScope.launch {
            listaCap = api.getCap(comune)
        }
    }

    suspend fun searchComuni(query: String): List<String> {
        return api.searchComuni(query)
    }
    // --------------------------

    var isLoading by mutableStateOf(false)
    var errorMessage by mutableStateOf<String?>(null)
    var registrationSuccess by mutableStateOf(false)
    
    var rememberMe by mutableStateOf(false)
    var savedEmail by mutableStateOf("")
    var savedPassword by mutableStateOf("")
    
    fun updateRememberMe(value: Boolean) {
        rememberMe = value
        storage?.saveString("remember_me", value.toString())
        if (!value) {
            // Se disabilitato, cancella subito tutto
            storage?.saveString("saved_email", "")
            storage?.saveString("saved_password", "")
            savedEmail = ""
            savedPassword = ""
        }
    }
    
    var currentUserRole by mutableStateOf<UserRole?>(null)
    var currentUserEmail by mutableStateOf<String?>(null)
    var currentUserFarmacista by mutableStateOf<Farmacista?>(null)
    var currentUserFarmacia by mutableStateOf<Farmacia?>(null)
    var currentMatches by mutableStateOf<List<Match>>(emptyList())
    var userReputation by mutableStateOf(UserReputation())
    var currentPreferiti by mutableStateOf<List<Preferito>>(emptyList())
    var notifications by mutableStateOf<List<Notification>>(emptyList())
    var unreadNotificationsCount by mutableStateOf(0)
    var actionErrorMessage by mutableStateOf<String?>(null)
    var successMessage by mutableStateOf<String?>(null)
    
    // Cache per i dettagli degli annunci confermati (per controllo sovrapposizioni)
    var confirmedShiftsDetails by mutableStateOf<List<AnnuncioTurno>>(emptyList())
    
    fun checkConflict(ad: AnnuncioTurno): Instant? {
        if (currentUserRole != UserRole.FARMACISTA) return null
        
        confirmedShiftsDetails.forEach { confirmed ->
            if (ad.id != confirmed.id && TimeUtils.isOverlap(
                ad.dataTurno, ad.oraInizio, ad.oraFine,
                confirmed.dataTurno, confirmed.oraInizio, confirmed.oraFine
            )) {
                return confirmed.dataTurno
            }
        }
        return null
    }
    
    // --- STATO ABBONAMENTO ---
    var subscriptionStatus: UserStatus by mutableStateOf(UserStatus(userId = "", isPremium = false, subscriptionType = SubscriptionType.FREE))
    
    fun fetchSubscriptionStatus() {
        val email = currentUserEmail ?: return
        val role = currentUserRole ?: return
        val myId = getUnivocoUtenteId() ?: email
        
        viewModelScope.launch {
            isLoading = true
            successMessage = null
            actionErrorMessage = null
            val myId = getUnivocoUtenteId() ?: email
            val targetUrl = "${redmcrosco.alex.farmatch.PAYMENT_API_URL}/v1/status/FAR_MATCH/$myId"
            println("DEBUG: Chiamata a: $targetUrl")
            
            try {
                val status = api.getSubscriptionStatus(myId)
                subscriptionStatus = status
                
                // Salva ultimo stato noto
                storage?.saveString("last_subscription_type", status.subscriptionType.name)
                storage?.saveString("last_is_premium", status.isPremium.toString())
                status.userRole?.let { storage?.saveString("last_user_role", it.name) }
                
                if (status.isPremium) {
                    val expDate = status.expiryDate
                    val expiryInfo = if (expDate != null) {
                        val expiry = Instant.fromEpochMilliseconds(expDate).toLocalDateTime(TimeZone.currentSystemDefault()).date
                        " fino al $expiry"
                    } else ""
                    successMessage = "Abbonamento ${status.subscriptionType} attivo$expiryInfo!"
                    println("DEBUG: Abbonamento PREMIUM trovato: ${status.subscriptionType} fino a $expiryInfo")
                } else {
                    successMessage = "Stato aggiornato: Account Base"
                    println("DEBUG: L'utente risulta in versione BASE")
                }
            } catch (e: Exception) {
                actionErrorMessage = "Errore connessione Hub: ${e.message}"
                println("DEBUG: ERRORE CRITICO verifica abbonamento: ${e.message}")
                e.printStackTrace()
            } finally {
                isLoading = false
            }
        }
    }
    
    private var storage: SettingsStorage? = null
    var selectedThemeColor by mutableStateOf(AppThemeColor.GREEN)
    var selectedLanguage by mutableStateOf("it")

    fun initStorage(context: Any?) {
        if (storage == null) {
            storage = createSettingsStorage(context)
            
            // Carica colore
            val savedColorName = storage?.getString("theme_color", AppThemeColor.GREEN.name)
            selectedThemeColor = try {
                AppThemeColor.valueOf(savedColorName ?: AppThemeColor.GREEN.name)
            } catch (e: Exception) {
                AppThemeColor.GREEN
            }
            
            // Carica lingua
            selectedLanguage = storage?.getString("app_language", "it") ?: "it"
            
            // Carica abbonamento (ultimo stato noto)
            val lastTypeStr = storage?.getString("last_subscription_type", "FREE") ?: "FREE"
            val lastType = try { SubscriptionType.valueOf(lastTypeStr) } catch(e: Exception) { SubscriptionType.FREE }
            val lastIsPremium = storage?.getString("last_is_premium", "false") == "true"
            val lastRoleStr = storage?.getString("last_user_role", "")
            val lastRole = try { if (!lastRoleStr.isNullOrEmpty()) UserRole.valueOf(lastRoleStr) else null } catch(e: Exception) { null }
            
            rememberMe = storage?.getString("remember_me", "false") == "true"
            savedEmail = storage?.getString("saved_email", "") ?: ""
            savedPassword = storage?.getString("saved_password", "") ?: ""
            
            if (rememberMe && savedEmail.isNotBlank() && savedPassword.isNotBlank()) {
                login(LoginRequest(savedEmail, savedPassword))
            }

            subscriptionStatus = UserStatus(
                userId = "", 
                isPremium = lastIsPremium, 
                subscriptionType = lastType,
                userRole = lastRole
            )
        }
    }

    fun updateThemeColor(color: AppThemeColor) {
        selectedThemeColor = color
        storage?.saveString("theme_color", color.name)
    }

    fun updateLanguage(lang: String) {
        selectedLanguage = lang
        storage?.saveString("app_language", lang)
    }

    fun sendDirectProposal(targetId: String, targetRuolo: String, message: String, onResult: (Boolean) -> Unit) {
        val myEmail = currentUserEmail ?: return
        val targetUnivoco = "${targetRuolo}_$targetId"
        viewModelScope.launch {
            val success = api.sendDirectProposal(targetUnivoco, targetRuolo, message, myEmail)
            onResult(success)
            if (success) refreshUserData()
        }
    }

    fun getUnivocoUtenteId(): String? {
        val role = currentUserRole ?: return null
        val numericId = (currentUserFarmacia?.id ?: currentUserFarmacista?.id) ?: currentUserEmail ?: return null
        return if (role == UserRole.FARMACISTA) "FARMACISTA_$numericId" else "FARMACIA_$numericId"
    }

    fun markNotificationsAsRead() {
        val myId = getUnivocoUtenteId() ?: return
        viewModelScope.launch {
            api.markNotificationsAsRead(myId)
            unreadNotificationsCount = 0
            notifications = notifications.map { it.copy(isRead = true) }
        }
    }

    fun deleteNotification(id: String) {
        viewModelScope.launch {
            val success = api.deleteNotification(id)
            if (success) {
                notifications = notifications.filter { it.id != id }
                unreadNotificationsCount = notifications.count { !it.isRead }
            }
        }
    }

    // --- STATO FILTRI HOME ---
    var filtriHome by mutableStateOf<Set<String>>(emptySet())
    var dashboardFiltro by mutableStateOf<String?>(null)

    fun updateDashboardFiltro(filtro: String) {
        dashboardFiltro = if (dashboardFiltro == filtro) null else filtro
    }

    fun login(request: LoginRequest) {
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                val response = api.login(request)
                if (response.success) {
                    // PROTEZIONE ADMIN
                    if (response.role == UserRole.ADMIN && response.email?.lowercase() != "farmatch00@gmail.com") {
                        errorMessage = "Accesso negato: Area riservata allo sviluppatore."
                        return@launch
                    }

                    currentUserRole = response.role
                    currentUserEmail = response.email

                    // Gestione "Ricordami"
                    if (rememberMe) {
                        storage?.saveString("remember_me", "true")
                        storage?.saveString("saved_email", request.email)
                        storage?.saveString("saved_password", request.password)
                    } else {
                        storage?.saveString("remember_me", "false")
                        storage?.saveString("saved_email", "")
                        storage?.saveString("saved_password", "")
                    }
                    
                    // IMPORTANTE: refreshUserData deve caricare l'ID prima di fetchSubscriptionStatus
                    refreshUserData().join() // Attendiamo il completamento del caricamento profilo
                } else {
                    errorMessage = response.message ?: "Credenziali non valide"
                }
            } catch (e: Exception) {
                errorMessage = "Errore di rete: ${e.message}"
            } finally {
                isLoading = false
            }
        }
    }

    var passwordResetLoading by mutableStateOf(false)

    suspend fun requestPasswordReset(email: String): PasswordResetResponse {
        passwordResetLoading = true
        return try {
            api.requestPasswordReset(email.trim())
        } catch (e: Exception) {
            PasswordResetResponse(success = false, message = "Errore di rete: ${e.message}")
        } finally {
            passwordResetLoading = false
        }
    }

    suspend fun confirmPasswordReset(email: String, code: String, newPassword: String): PasswordResetResponse {
        passwordResetLoading = true
        return try {
            api.confirmPasswordReset(email.trim(), code.trim(), newPassword)
        } catch (e: Exception) {
            PasswordResetResponse(success = false, message = "Errore di rete: ${e.message}")
        } finally {
            passwordResetLoading = false
        }
    }

    fun refreshUserData() = viewModelScope.launch {
        val email = currentUserEmail ?: return@launch
        val role = currentUserRole ?: return@launch
        try {
            if (role == UserRole.FARMACISTA) {
                currentUserFarmacista = api.getFarmacistaByEmail(email)
            } else if (role == UserRole.FARMACIA) {
                currentUserFarmacia = api.getFarmaciaByEmail(email)
            }
            
            val myId = getUnivocoUtenteId() ?: email
            currentMatches = api.getMatches(email, role.name)
            
            val histCompleted = if (role == UserRole.FARMACISTA) currentUserFarmacista?.storicoCompletati ?: 0 else currentUserFarmacia?.storicoCompletati ?: 0
            val histCancelled = if (role == UserRole.FARMACISTA) currentUserFarmacista?.storicoAnnullati ?: 0 else currentUserFarmacia?.storicoAnnullati ?: 0
            
            userReputation = ReputationUtils.calculateReputation(currentMatches, histCompleted, histCancelled)
            currentPreferiti = api.getPreferiti(myId)
            notifications = api.getNotifications(myId)
            unreadNotificationsCount = notifications.count { !it.isRead }
            
            // Carica dettagli dei turni confermati per check sovrapposizioni
            if (role == UserRole.FARMACISTA) {
                val confirmedIds = currentMatches.filter { it.stato == MatchStato.CONFERMATO }.map { it.annuncioId }
                val details = mutableListOf<AnnuncioTurno>()
                confirmedIds.forEach { id ->
                    api.getAnnuncioById(id)?.let { details.add(it) }
                }
                confirmedShiftsDetails = details
            }
            
            // Ora che abbiamo caricato il profilo (e l'ID numerico), verifichiamo l'abbonamento
            fetchSubscriptionStatus()
            
            println("DEBUG: Profile and Subscription loaded for $myId")
        } catch (e: Exception) {
            println("DEBUG: Error refreshing user data: ${e.message}")
        }
    }

    fun respondToMatch(matchId: String, action: String, onResult: (Boolean) -> Unit = {}) {
        val email = currentUserEmail ?: return
        val role = currentUserRole?.name ?: return
        viewModelScope.launch {
            val (success, errorMsg) = api.respondToMatch(matchId, action, email, role)
            if (success) {
                actionErrorMessage = null
                refreshUserData() // Ricarica per vedere lo stato aggiornato
            } else {
                actionErrorMessage = errorMsg
            }
            onResult(success)
        }
    }

    fun updateAnnuncio(annuncio: AnnuncioTurno, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            isLoading = true
            try {
                val success = api.updateAnnuncio(annuncio.id ?: "", annuncio)
                onResult(success)
                if (success) refreshUserData()
            } catch (e: Exception) {
                onResult(false)
            } finally {
                isLoading = false
            }
        }
    }

    fun logout() {
        currentUserRole = null
        currentUserEmail = null
        currentUserFarmacista = null
        currentUserFarmacia = null
        
        // Pulizia credenziali salvate al logout esplicito
        if (!rememberMe) {
            storage?.saveString("saved_email", "")
            savedEmail = ""
        }
        storage?.saveString("saved_password", "")
        savedPassword = ""
    }

    fun updateProfile(farmacista: Farmacista) {
        val email = currentUserEmail ?: return
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                val success = api.updateFarmacistaProfilo(email, farmacista)
                if (success) {
                    refreshUserData()
                } else {
                    errorMessage = "Errore durante l'aggiornamento del profilo"
                }
            } catch (e: Exception) {
                errorMessage = "Errore di connessione"
            } finally {
                isLoading = false
            }
        }
    }

    fun updateFarmaciaProfile(farmacia: Farmacia) {
        val email = currentUserEmail ?: return
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                val success = api.updateFarmaciaProfilo(email, farmacia)
                if (success) {
                    refreshUserData()
                } else {
                    errorMessage = "Errore durante l'aggiornamento del profilo"
                }
            } catch (e: Exception) {
                errorMessage = "Errore di connessione"
            } finally {
                isLoading = false
            }
        }
    }

    fun pubblicaAnnuncio(annuncio: AnnuncioTurno, onResult: (String) -> Unit = {}) {
        viewModelScope.launch {
            isLoading = true
            try {
                val result = api.createAnnuncio(annuncio)
                if (result == "CONFLICT") {
                    errorMessage = "Attenzione: Questo turno esiste già per lo stesso giorno e orario."
                } else if (result == "ERROR") {
                    errorMessage = "Impossibile salvare l'annuncio"
                }
                onResult(result)
            } catch (e: Exception) {
                errorMessage = "Impossibile salvare l'annuncio"
                onResult("ERROR")
            } finally {
                isLoading = false
            }
        }
    }

    fun pubblicaBatchAnnunci(request: BatchAnnuncioRequest, onResult: (String) -> Unit = {}) {
        viewModelScope.launch {
            isLoading = true
            try {
                val result = api.createBatchAnnunci(request)
                if (result == "CONFLICT") {
                    errorMessage = "Alcuni dei turni erano già presenti e sono stati saltati."
                } else if (result == "ERROR") {
                    errorMessage = "Errore durante la creazione dei turni a blocchi."
                }
                onResult(result)
            } catch (e: Exception) {
                errorMessage = "Impossibile salvare il blocco di annunci"
                onResult("ERROR")
            } finally {
                isLoading = false
            }
        }
    }

    fun registerFarmacista(farmacista: Farmacista) {
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                val success = api.registerFarmacista(farmacista)
                if (success) {
                    registrationSuccess = true
                } else {
                    errorMessage = "Registrazione fallita. Controlla i dati o riprova più tardi."
                }
            } catch (e: Exception) {
                errorMessage = "Errore di connessione: ${e.message}"
            } finally {
                isLoading = false
            }
        }
    }

    fun registerFarmacia(farmacia: Farmacia) {
        viewModelScope.launch {
            isLoading = true
            errorMessage = null
            try {
                val success = api.registerFarmacia(farmacia)
                if (success) {
                    registrationSuccess = true
                } else {
                    errorMessage = "Errore: Dati già presenti o non validi."
                }
            } catch (e: Exception) {
                errorMessage = "Il server non risponde. Verifica ngrok."
            } finally {
                isLoading = false
            }
        }
    }

    suspend fun getAnnunciUtente(): List<AnnuncioTurno> {
        return api.getAnnunci(email = currentUserEmail)
    }

    fun eliminaAnnuncio(id: String, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            val success = api.deleteAnnuncio(id)
            onResult(success)
        }
    }

    fun eliminaBatchAnnunci(ids: List<String>, onResult: (Boolean) -> Unit) {
        viewModelScope.launch {
            val success = api.deleteBatchAnnunci(ids)
            onResult(success)
        }
    }

    suspend fun getAnnuncioByIdSuspend(id: String): AnnuncioTurno? {
        return api.getAnnuncioById(id)
    }

    fun getAnnuncioById(id: String, onResult: (AnnuncioTurno?) -> Unit) {
        viewModelScope.launch {
            val annuncio = api.getAnnuncioById(id)
            onResult(annuncio)
        }
    }

    fun getFarmacistaByEmail(email: String, onResult: (Farmacista?) -> Unit) {
        viewModelScope.launch {
            val farmacista = api.getFarmacistaByEmail(email)
            onResult(farmacista)
        }
    }

    fun getFarmaciaByEmail(email: String, onResult: (Farmacia?) -> Unit) {
        viewModelScope.launch {
            val farmacia = api.getFarmaciaByEmail(email)
            onResult(farmacia)
        }
    }

    fun getFarmacistaById(id: String, onResult: (Farmacista?) -> Unit) {
        viewModelScope.launch {
            val farmacista = api.getFarmacistaById(id)
            onResult(farmacista)
        }
    }

    fun getFarmaciaById(id: String, onResult: (Farmacia?) -> Unit) {
        viewModelScope.launch {
            val farmacia = api.getFarmaciaById(id)
            onResult(farmacia)
        }
    }

    // --- LOGICA PREFERITI ---
    fun togglePreferito(targetId: String, targetRuolo: String) {
        val myId = (currentUserFarmacia?.userId ?: currentUserFarmacista?.userId)
        val myEmail = currentUserEmail
        
        println("DEBUG: Richiesta togglePreferito")
        println("DEBUG: myId=$myId, myEmail=$myEmail, targetId=$targetId, targetRuolo=$targetRuolo")
        
        val idToUse = myId ?: myEmail
        
        if (idToUse == null) {
            println("DEBUG: ERRORE - Nessun identificativo utente trovato (ID ed Email nulli)")
            return
        }

        viewModelScope.launch {
            println("DEBUG: Eseguo chiamata API togglePreferito per $idToUse -> $targetId")
            try {
                val success = api.togglePreferito(Preferito(
                    utenteId = idToUse,
                    targetId = targetId,
                    targetRuolo = targetRuolo
                ))
                if (success) {
                    currentPreferiti = api.getPreferiti(idToUse)
                    println("DEBUG: Toggle riuscito, preferiti aggiornati (totale: ${currentPreferiti.size})")
                } else {
                    println("DEBUG: Il server ha risposto FALSE al toggle")
                }
            } catch (e: Exception) {
                println("DEBUG: Eccezione durante togglePreferito: ${e.message}")
            }
        }
    }

    fun isFavorite(targetId: String): Boolean {
        return currentPreferiti.any { it.targetId == targetId }
    }

    suspend fun getEmergencyAnnunci(): List<AnnuncioTurno> {
        val role = currentUserRole ?: return emptyList()
        val email = currentUserEmail ?: return emptyList()
        val provincia = currentUserFarmacista?.provinciaOperativita ?: currentUserFarmacia?.provincia
        val comune = currentUserFarmacista?.comuneOperativita ?: currentUserFarmacia?.citta
        
        return try {
            api.getAnnunci(role = role.name, provincia = provincia, comune = comune, email = email)
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getAnnunciFarmacia(email: String): List<AnnuncioTurno> {
        return api.getAnnunci(email).filter { it.stato == AnnuncioStato.APERTO }
    }

    suspend fun searchFarmacisti(
        query: String = "",
        comune: String = "",
        cap: String = "",
        startDate: Long? = null,
        endDate: Long? = null,
        competenza: String = ""
    ): List<Farmacista> {
        return try {
            api.searchFarmacisti(query, comune, cap, startDate, endDate, currentUserEmail, competenza)
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun searchFarmacie(
        query: String = "",
        regione: String = "",
        provincia: String = "",
        comune: String = "",
        cap: String = "",
        startDate: Long? = null,
        endDate: Long? = null
    ): List<Farmacia> {
        return try {
            api.searchFarmacie(query, regione, provincia, comune, cap, startDate, endDate, currentUserEmail)
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun getStorico(): List<StoricoEntry> {
        val role = currentUserRole ?: return emptyList()
        val myId = (currentUserFarmacia?.id ?: currentUserFarmacista?.id) ?: return emptyList()
        
        return try {
            api.getStorico(myId, role.name)
        } catch (e: Exception) {
            emptyList()
        }
    }

    suspend fun creaFatturaProforma(annuncioIds: List<String>, farmaciaId: String): FatturaEntry? {
        val fId = currentUserFarmacista?.id ?: return null
        return api.creaFatturaProforma(annuncioIds, fId, farmaciaId)
    }

    suspend fun getStoricoFatture(): List<FatturaEntry> {
        val myId = (currentUserFarmacia?.id ?: currentUserFarmacista?.id) ?: return emptyList()
        val role = currentUserRole ?: return emptyList()
        return api.getStoricoFatture(myId, role.name)
    }
}
